import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import CommentsWrap from '@/components/comments_components/CommentsWrap';
import { Spinner } from '@/components/common/Spinner';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import useQuestions from '@/hooks/useQuestions';
import { AttachmentProps } from '@/models/Attachment';
import { EventProps } from '@/models/Event';
import { EventTypeProps } from '@/models/EventType';
import { QuestionProps } from '@/models/Question';
import { Translations } from '@/utils/translations/Translations';
import { isEventValid } from '@/utils/eventUtil';

import Event from './Event';

// A slot represents one event form's position in the list. Its key is generated once
// and never changes for the slot's lifetime, so when its pending (eventId == null) form
// creates itself and gets a real id, React keeps reusing the same component instance
// instead of unmounting/remounting it.
type EventSlot = { key: string; eventId?: number };

function AddEvent({
  addUpload,
  canEdit,
  eventType,
  translatedText,
}: {
  addUpload: () => void;
  canEdit: boolean;
  eventType: EventTypeProps | null;
  translatedText: Translations;
}) {
  const eventTypeNames: { [key: number]: string } = {
    1: 'Arbor Day',
    2: translatedText.event.treeDay,
    3: translatedText.event.celebration,
    4: 'Training',
    5: 'Observance',
    6: 'Observance',
    7: 'Project',
    8: 'Project',
    9: 'Event',
    10: 'Experience',
    11: 'Arbor Day',
    14: 'Meeting',
    16: 'Arbor Day',
  };

  return (
    <Button
      sx={{ marginTop: '15px' }}
      onClick={addUpload}
      size='small'
      variant='secondary'
      disabled={!canEdit}
    >
      {`Add ${eventType != null ? eventTypeNames[eventType.id] : 'Event'}`}
    </Button>
  );
}

// Top-level component for the "event" question type: loads this question's events,
// owns the save/delete/validity logic against the API, and lays out the list of
// event-form "slots" - one Event per already-saved event, plus (when editable) a
// pending slot that creates itself on render.
//
// QuestionSwitch already hides this component entirely when its parent-question
// condition isn't met, so no visibility gating is needed here.
export default function EventQuestion({ question }: { question: QuestionProps }) {
  const { deleteEvent, createEvent, updateEvent, getSpecificQuestionAnswer } = useQuestions();
  const {
    answers,
    currentApplication,
    canEdit,
    saveApplicationAnswer2,
    updateAnswers,
    allEvents,
    updateCurrentEvents,
  } = useContext(CurrentApplicationContext);

  const { setAlert } = useContext(SnackbarContext);
  const { eventTypes } = useContext(ConfigContext);
  const { locale, translatedText } = useContext(LocaleContext);

  const eventType = useMemo(
    () => eventTypes?.find((x: EventTypeProps) => x.id == question?.eventType?.id) ?? null,
    [eventTypes, question?.eventType?.id]
  );

  const questionEventList: EventProps[] | undefined = useMemo(() => {
    if (allEvents == null) return undefined;

    if (allEvents.length === 0) return [];

    const fetchedEvents: EventProps[] = Array.from(
      new Map(allEvents.filter((e) => e.questionId == question.id).map((e) => [e.id, e])).values()
    );

    return fetchedEvents;
  }, [allEvents, question.id]);

  const questionAnswer = useMemo(() => answers[question.id], [answers, question.id]);

  const questionAnswerRef = useRef(questionAnswer);
  questionAnswerRef.current = questionAnswer;

  const saveApplicationAnswer2Ref = useRef(saveApplicationAnswer2);
  saveApplicationAnswer2Ref.current = saveApplicationAnswer2;

  const updateEventList = useCallback(
    (eventInfo: EventProps) => {
      if (allEvents != null && allEvents.length > 0) {
        const copy = [...allEvents];
        const idx = copy.findIndex((e) => e.id === eventInfo.id);
        if (idx === -1) copy.push(eventInfo);
        else copy[idx] = eventInfo;
        updateCurrentEvents(copy);
      } else {
        updateCurrentEvents([eventInfo]);
      }
    },
    [allEvents, updateCurrentEvents]
  );

  // The single source of truth for whether this question's answer should read
  // 'completed': every event belonging to it must be valid. Used after every save
  // and delete, and once on load, so the answer never drifts from the event data.
  const syncQuestionCompletion = useCallback(
    async (events: EventProps[], filesOverride?: AttachmentProps[]) => {
      const questionEvents = events.filter((e) => e.questionId == question.id);
      const files = filesOverride ?? questionAnswerRef.current?.files;
      const validEvents =
        questionEvents.length > 0 &&
        questionEvents.every((e) => isEventValid(e, question.eventType, files));

      const target = validEvents ? 'completed' : null;
      if (target === (questionAnswerRef.current?.responseString ?? null)) return;

      await saveApplicationAnswer2Ref.current(question, target);
    },
    [question]
  );

  const saveEvent = useCallback(
    async (event: EventProps) => {
      if (event == null) return;

      const newEvent = event.id == null;

      try {
        const result = newEvent ? await createEvent(event, question.id) : await updateEvent(event);
        const savedEvent =
          result != null && typeof result === 'object' && 'id' in result
            ? { ...event, id: result.id }
            : event;
        const normalizedSavedEvent = {
          ...savedEvent,
          questionId: savedEvent.questionId ?? question.id,
          applicationId: savedEvent.applicationId ?? currentApplication.id,
        };

        updateEventList(normalizedSavedEvent);

        const eventsCopy = allEvents != null && allEvents.length > 0 ? [...allEvents] : [];
        const existingIdx = eventsCopy.findIndex((e) => e.id === normalizedSavedEvent.id);
        if (existingIdx === -1) eventsCopy.push(normalizedSavedEvent);
        else eventsCopy[existingIdx] = normalizedSavedEvent;

        await syncQuestionCompletion(eventsCopy);
      } catch (error: unknown) {
        setAlert({
          message: translatedText.common.genericError,
          severity: 'error',
        });
        throw error;
      }
    },
    [
      setAlert,
      translatedText,
      allEvents,
      currentApplication.id,
      createEvent,
      updateEvent,
      question,
      updateEventList,
      syncQuestionCompletion,
    ]
  );

  // Re-checks completion on load and whenever this question's attached files change.
  // Proof uploads go through DataUploadWrap, a completely separate save path from
  // saveEvent, so this is the only thing that notices "proof was just added" and
  // flips the answer to 'completed' for event types that require it.
  useEffect(() => {
    if (questionEventList === undefined) return;
    if (locale == null || currentApplication.id == null) return;

    void syncQuestionCompletion(questionEventList);
  }, [
    currentApplication.id,
    locale,
    questionAnswer?.files,
    questionEventList,
    syncQuestionCompletion,
  ]);

  const removeDeletedEventFromEventList = useCallback(
    (eventId: number) => {
      if (allEvents == null || allEvents.length === 0) return [];

      const updatedCurrentEvents = allEvents.filter((e: EventProps) => e.id !== eventId);

      updateCurrentEvents(updatedCurrentEvents);

      return updatedCurrentEvents;
    },
    [allEvents, updateCurrentEvents]
  );

  const refreshQuestionAnswer = useCallback(async (): Promise<AttachmentProps[] | undefined> => {
    const updatedAnswer = await getSpecificQuestionAnswer(question.id);
    if (updatedAnswer != null) {
      updateAnswers(updatedAnswer);
      return updatedAnswer.files;
    }

    return undefined;
  }, [getSpecificQuestionAnswer, question.id, updateAnswers]);

  const handleDelete = useCallback(
    async (eventInfo: EventProps) => {
      if (eventInfo?.id == null) return;

      if (locale == null) return;

      await deleteEvent(eventInfo.id);

      const updatedCurrentEvents = removeDeletedEventFromEventList(eventInfo.id);
      const refreshedFiles = await refreshQuestionAnswer();

      await syncQuestionCompletion(updatedCurrentEvents, refreshedFiles);
    },
    [locale, deleteEvent, removeDeletedEventFromEventList, refreshQuestionAnswer, syncQuestionCompletion]
  );

  const [slots, setSlots] = useState<EventSlot[]>([]);
  const slotCounterRef = useRef<number>(0);
  const makeSlotKey = useCallback(
    () => `${question.id}-slot-${slotCounterRef.current++}`,
    [question.id]
  );

  useEffect(() => {
    setSlots([]);
  }, [question.id]);

  // Keep `slots` in sync with the persisted events for this question: attach newly
  // saved events to whichever slot is still pending (eventId == null), add slots for
  // events that were already saved when this question first loaded, drop slots for
  // events that got deleted elsewhere, and make sure there's always at least one
  // (possibly pending) slot to show when the question is editable.
  useEffect(() => {
    if (questionEventList == null) return;

    setSlots((prev) => {
      const currentIds = new Set(questionEventList.map((e) => e.id));
      let next = prev.filter((s) => s.eventId == null || currentIds.has(s.eventId));

      const trackedIds = new Set(
        next.map((s) => s.eventId).filter((id): id is number => id != null)
      );
      const untracked = questionEventList.filter((e) => e.id != null && !trackedIds.has(e.id));

      for (const event of untracked) {
        const pendingIdx = next.findIndex((s) => s.eventId == null);
        if (pendingIdx !== -1) {
          next = next.map((s, i) => (i === pendingIdx ? { ...s, eventId: event.id! } : s));
        } else {
          next = [...next, { key: makeSlotKey(), eventId: event.id! }];
        }
      }

      if (next.length === 0 && canEdit) {
        next = [{ key: makeSlotKey(), eventId: undefined }];
      }

      return next;
    });
  }, [questionEventList, canEdit, makeSlotKey]);

  const hasPendingSlot = slots.some((s) => s.eventId == null);

  const canAddMoreEvents =
    questionEventList != null &&
    !hasPendingSlot &&
    questionEventList.length > 0 &&
    eventType?.maxInstances != null &&
    eventType.maxInstances > 1 &&
    eventType.maxInstances > questionEventList.length;

  const handleAddEvent = useCallback(() => {
    setSlots((prev) => {
      if (prev.some((s) => s.eventId == null)) return prev;
      return [...prev, { key: makeSlotKey(), eventId: undefined }];
    });
  }, [makeSlotKey]);

  return (
    <Box display='flex' width='100%' justifyContent='space-between' alignItems='start'>
      <Box minWidth='600px' sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {questionEventList === undefined ? (
          <Spinner type='circular' />
        ) : (
          <>
            {slots.map((slot) => (
              <Event
                key={slot.key}
                question={question}
                eventType={eventType}
                eventId={slot.eventId}
                onChange={saveEvent}
                onDelete={handleDelete}
              />
            ))}
            {canAddMoreEvents && (
              <Box>
                <Typography style={{ maxWidth: '600px' }}>
                  {translatedText.event.helperEvent}
                </Typography>
                <AddEvent
                  addUpload={handleAddEvent}
                  canEdit={canEdit}
                  eventType={eventType}
                  translatedText={translatedText}
                />
              </Box>
            )}
          </>
        )}
      </Box>
      <CommentsWrap question={question} />
    </Box>
  );
}
